export type VideoProvider = "youtube" | "vimeo" | "file";

export function detectVideoProvider(url: string): VideoProvider {
  const u = url.toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
  if (u.includes("vimeo.com")) return "vimeo";
  return "file";
}

export function youtubeEmbed(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") {
      const id = u.pathname.replace(/^\//, "");
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) {
        return `https://www.youtube-nocookie.com${u.pathname}`;
      }
      const id = u.searchParams.get("v");
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    return null;
  } catch {
    return null;
  }
}

export function vimeoEmbed(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("player.vimeo.com")) return url;
    const id = u.pathname.split("/").filter(Boolean).pop();
    return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
  } catch {
    return null;
  }
}

export function resolveVideoEmbed(
  rawUrl: string,
  providerHint?: string
): { provider: VideoProvider; embedSrc: string | null; fileSrc: string } {
  const provider =
    (providerHint as VideoProvider) || detectVideoProvider(rawUrl);
  let embedSrc: string | null = null;
  if (provider === "youtube") embedSrc = youtubeEmbed(rawUrl);
  else if (provider === "vimeo") embedSrc = vimeoEmbed(rawUrl);
  return { provider, embedSrc, fileSrc: provider === "file" ? rawUrl : "" };
}

/** YouTube video id for loop playlist param. */
function youtubeVideoId(embedUrl: string): string | null {
  const m = embedUrl.match(/\/embed\/([^?&/]+)/);
  return m?.[1] ?? null;
}

/** Muted autoplay background embed (privacy-friendly nocookie host). */
export function youtubeBackgroundEmbed(url: string): string | null {
  const base = youtubeEmbed(url);
  if (!base) return null;
  const id = youtubeVideoId(base);
  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    loop: "1",
    controls: "0",
    playsinline: "1",
    rel: "0",
    modestbranding: "1",
    showinfo: "0",
    iv_load_policy: "3",
    disablekb: "1",
    fs: "0",
  });
  if (id) params.set("playlist", id);
  return `${base}?${params.toString()}`;
}

export function vimeoBackgroundEmbed(url: string): string | null {
  const base = vimeoEmbed(url);
  if (!base) return null;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}autoplay=1&muted=1&loop=1&background=1&controls=0&title=0&byline=0&portrait=0`;
}

export function resolveBackgroundVideo(
  rawUrl: string,
  providerHint?: string
): { provider: VideoProvider; embedSrc: string | null; fileSrc: string } {
  const provider =
    (providerHint as VideoProvider) || detectVideoProvider(rawUrl);
  let embedSrc: string | null = null;
  if (provider === "youtube") embedSrc = youtubeBackgroundEmbed(rawUrl);
  else if (provider === "vimeo") embedSrc = vimeoBackgroundEmbed(rawUrl);
  return { provider, embedSrc, fileSrc: provider === "file" ? rawUrl : "" };
}
